import { ReactElement, useEffect, useState } from "react";
import BaseDatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";

import { useMultiStyleConfig, Box } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";

import { IconDropdown } from "@components/common";

import RangeDateLabel from "./RangeDateLabel";
import type { TDatePickerMode, TRangeDateValue, TSingleDateValue, TCalendarSelectProps } from "./types";
import { registerLocales } from "./utils/locales";
import createRenderCustomHeader from "./utils/renderCustomHeader";

registerLocales();

const Calendar = <T extends TDatePickerMode>({
  id,
  mode,
  value,
  onChange,
  minDate,
  maxDate,
  isDisabled,
  locale,
  selectYearFrom,
  selectYearTo,
  filterDate,
  isOpen,
  onToggleOpen,
  onClose,
}: TCalendarSelectProps<T>): ReactElement => {
  const { t } = useTranslation();
  const [rangeValue, setRangeValue] = useState<TRangeDateValue>([undefined, undefined]);

  const styles = useMultiStyleConfig("DatePicker", {});

  useEffect(() => {
    if (!isOpen && mode === "range") {
      setRangeValue([value?.[0], value?.[1]]);
    }
  }, [isOpen, mode, value]);

  const handleChange = (date: Date | [Date | null, Date | null] | null) => {
    if (Array.isArray(date) && mode === "range") {
      const typedOnChange = onChange as (val: TRangeDateValue) => void;
      const dateValue: TRangeDateValue = [date[0] ?? undefined, date[1] ?? undefined];
      setRangeValue(dateValue);

      if (!dateValue[0] || !dateValue[1]) {
        return;
      }
      typedOnChange?.(dateValue);
      onToggleOpen(false);
    }
    if (!Array.isArray(date) && mode !== "range") {
      const typedOnChange = onChange as (val: TSingleDateValue) => void;
      typedOnChange?.((date ?? undefined) as TSingleDateValue);
      onToggleOpen(false);
    }
  };

  return (
    <IconDropdown
      id={id}
      isDisabled={isDisabled}
      isOpen={isOpen}
      onToggleOpen={onToggleOpen}
      onClose={() => {
        onToggleOpen(false);
        onClose?.();
      }}
      icon="calendar"
      placement="bottom-end"
      offset={[0, 10]}
      title={t("common-date_picker-calendar")}
      isLazy
    >
      {mode === "range" && <RangeDateLabel isEndDate={!!rangeValue[0] && !rangeValue[1]} />}
      <Box sx={styles.calendar}>
        <BaseDatePicker
          inline
          disabledKeyboardNavigation
          calendarStartDay={1}
          selectsRange={mode === "range"}
          selected={mode !== "range" ? (value as Date) : null}
          startDate={mode === "range" ? rangeValue?.[0] : undefined}
          endDate={mode === "range" ? rangeValue?.[1] : undefined}
          minDate={minDate}
          maxDate={maxDate}
          renderCustomHeader={createRenderCustomHeader({ locale, selectYearFrom, selectYearTo, minDate, maxDate, id })}
          onChange={handleChange}
          filterDate={filterDate}
          locale={locale}
        />
      </Box>
    </IconDropdown>
  );
};
export default Calendar;
